"use client";

import { PlanInquiryModal } from "@/components/plan-inquiry/plan-inquiry-modal";
import type { Plan } from "@/services/plans/types";
import { BIO_COPY } from "../bio-site.copy";
import { HIGHLIGHT_MARK_CLASS } from "../lib/highlight-mark";

export interface BioCollabBlockProps {
  /** `null` quando não há plano de Collab publicado — a busca falhou ou o plano saiu do ar. */
  readonly collabPlan: Plan | null;
}

/**
 * A forma da pílula do CTA — tamanho, tipografia e o anel de foco só por
 * teclado —, tirada do fallback abaixo (que já bate com o desenho aprovado)
 * para os dois caminhos (com e sem plano) desenharem o mesmo botão.
 */
const COLLAB_PILL_CLASS =
  "min-h-12 px-[22px] text-[15px] font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-tami-light";

/**
 * O `TamiButtonPrimary` (usado por dentro do `PlanInquiryModal`) pinta fundo
 * e texto com `bg-[var(--tami-green)]`/`text-[var(--tami-dark-gray)]`, e
 * essas duas variáveis CSS só existem em `apps/web/src/app/[lang]/(home)/styles/home.style.css`,
 * importada só pela home. Fora da home elas não existem: o fundo fica
 * transparente e o texto herda branco, sumindo a pílula verde. Também traz
 * `border border-black`, `px-8 py-3 text-base` (tamanho da home, maior que o
 * da pílula aprovada) e `focus:ring-*` (anel em qualquer clique do mouse,
 * não só por teclado). As classes abaixo sobrescrevem tudo isso pelo
 * `cn`/`tailwind-merge` do `TamiButton` (mesmo grupo de classe —
 * `background-color`, `padding`, `font-size`, `ring-width` — valor por
 * último ganha, e `className` é o último argumento do `cn` do `TamiButton`)
 * — confirmado lendo o merge antes de aplicar aqui, sem reescrever o botão
 * de marca. `focus:ring-0`/`focus:ring-offset-0` zeram o anel herdado do
 * `TamiButton` (mesmo grupo, mesmo modificador `focus:`); o `COLLAB_PILL_CLASS`
 * então desenha o único anel de foco visível, e só por teclado.
 */
const COLLAB_CTA_OVERRIDE_CLASS = `border-0 bg-tami-green text-tami-dark hover:bg-tami-green-hover focus:ring-0 focus:ring-offset-0 ${COLLAB_PILL_CLASS}`;

/**
 * A porta de quem gerencia vários creators.
 *
 * Sem plano de Collab publicado, o bloco não some: o botão vira o WhatsApp
 * do suporte. Sumir deixaria a página sem resposta para "e se eu for uma
 * Collab?" — e quem chega pela bio não volta depois para procurar.
 *
 * A pílula verde é escrita direto aqui (e não com o `TamiButton`) porque o
 * botão do plano (`PlanInquiryModal`) já usa o `TamiButton` por dentro; esta
 * é só a forma do botão de fallback, tirada do HTML aprovado
 * (`docs/design/bio-links/direcao-3-estudio.html`, `.collab-btn`).
 */
export function BioCollabBlock({ collabPlan }: BioCollabBlockProps) {
  const { collab, support } = BIO_COPY;

  return (
    <section aria-labelledby="bio-collab-title" className="mt-[34px] bg-tami-dark px-6 pb-[34px] pt-9">
      <h2 id="bio-collab-title" className="mb-3.5 text-[26px] font-bold leading-[1.15] tracking-tight text-white">
        {collab.title}
      </h2>

      <p className="mb-6 max-w-[320px] text-[15px] leading-[1.6] text-tami-light">
        {collab.description.before}
        <mark className={HIGHLIGHT_MARK_CLASS}>{collab.description.highlight}</mark>
        {collab.description.after}
      </p>

      {collabPlan ? (
        <PlanInquiryModal
          plan={collabPlan}
          ctaLabel={collab.ctaLabel}
          className={COLLAB_CTA_OVERRIDE_CLASS}
        />
      ) : (
        <a
          href={support.whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center justify-center rounded-full bg-tami-green text-tami-dark no-underline transition-[filter] duration-150 hover:brightness-105 motion-reduce:transition-none ${COLLAB_PILL_CLASS}`}
        >
          {collab.ctaLabel}
        </a>
      )}
    </section>
  );
}
